import {
  BookOpenText,
  CreditCard,
  Download,
  Heart,
  History,
  KeyRound,
  ReceiptText,
  RefreshCw,
  ShieldCheck,
  ShoppingBag,
  UserRound,
  WalletCards,
} from "lucide-react";
import {
  type FormEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { trackEvent } from "./analytics";
import {
  type AccountFavoriteRecord,
  type AccountSummary,
  ApiRequestError,
  type CreditLedgerRecord,
  deleteAccount,
  downloadAccountExport,
  type GenerationRequest,
  loadAccountCredits,
  loadAccountFavorites,
  loadAccountNames,
  loadAccountOrders,
  loadAccountRefunds,
  loadAccountSummary,
  loadSiteConfig,
  type OrderRecord,
  type RefundRequestRecord,
  type SiteConfigResponse,
  submitRefundRequest,
  updateAccountProfile,
} from "./api";
import { PurchaseModal } from "./purchase";
import "./account-center.css";
import "./account-filters.css";

type Tab = "overview" | "names" | "favorites" | "orders" | "refunds" | "credits" | "security";
type RecordTab = Exclude<Tab, "overview" | "security">;
const FILTERS: Record<RecordTab, string[]> = {
  names: ["queued", "processing", "retrying", "completed", "failed_refunded"],
  favorites: ["completed"],
  orders: ["pending", "expired", "paid", "partially_refunded", "refunded"],
  refunds: ["submitted", "reviewing", "provider_pending", "succeeded", "failed", "rejected"],
  credits: [
    "purchase",
    "generation_reserved",
    "generation_refunded",
    "admin_adjustment",
    "payment_refund",
  ],
};
const STATUS_LABELS: Record<string, string> = {
  queued: "排队中",
  processing: "处理中",
  retrying: "重试中",
  completed: "已完成",
  failed_refunded: "失败已返还",
  pending: "待支付",
  expired: "已过期",
  paid: "已支付",
  partially_refunded: "部分退款",
  refunded: "已退款",
  submitted: "已提交",
  reviewing: "审核中",
  provider_pending: "支付方处理中",
  succeeded: "退款成功",
  failed: "退款失败",
  rejected: "已驳回",
  purchase: "购买入账",
  generation_reserved: "取名扣除",
  generation_refunded: "取名返还",
  admin_adjustment: "管理员调整",
  payment_refund: "退款扣回",
};

const TABS: Array<{ id: Tab; zh: string; en: string; icon: typeof UserRound }> = [
  { id: "overview", zh: "账户概览", en: "Overview", icon: UserRound },
  { id: "names", zh: "取名记录", en: "Name history", icon: BookOpenText },
  { id: "favorites", zh: "我的收藏", en: "Saved names", icon: Heart },
  { id: "orders", zh: "购买记录", en: "Orders", icon: ReceiptText },
  { id: "refunds", zh: "退款记录", en: "Refunds", icon: RefreshCw },
  { id: "credits", zh: "次数流水", en: "Credit ledger", icon: WalletCards },
  { id: "security", zh: "账户安全", en: "Security", icon: KeyRound },
];

function money(currency: string, amountMinor: number) {
  return new Intl.NumberFormat(currency.toLowerCase() === "cny" ? "zh-CN" : "en-US", {
    style: "currency",
    currency: currency.toUpperCase(),
  }).format(amountMinor / 100);
}

function time(value: number, language: "zh" | "en") {
  return new Date(value).toLocaleString(language === "zh" ? "zh-CN" : "en-US");
}

function nextTab(): Tab {
  const value = new URLSearchParams(window.location.search).get("tab");
  return TABS.some((item) => item.id === value) ? (value as Tab) : "overview";
}

export function AccountCenter({ language }: { language: "zh" | "en" }) {
  const zh = language === "zh";
  const [tab, setTab] = useState<Tab>(nextTab);
  const [summary, setSummary] = useState<AccountSummary | null>(null);
  const [config, setConfig] = useState<SiteConfigResponse | null>(null);
  const [names, setNames] = useState<GenerationRequest[]>([]);
  const [favorites, setFavorites] = useState<AccountFavoriteRecord[]>([]);
  const [orders, setOrders] = useState<OrderRecord[]>([]);
  const [refunds, setRefunds] = useState<RefundRequestRecord[]>([]);
  const [credits, setCredits] = useState<CreditLedgerRecord[]>([]);
  const [cursors, setCursors] = useState<Record<string, string | null>>({});
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState("");
  const [purchaseOpen, setPurchaseOpen] = useState(false);
  const [refundOrder, setRefundOrder] = useState<
    | (Pick<OrderRecord, "id" | "currency" | "amountMinor" | "refundedAmountMinor"> & {
        reason?: string;
        details?: string;
      })
    | null
  >(null);
  const [siteFilter, setSiteFilter] = useState<"" | "cn" | "en">("");
  const [statuses, setStatuses] = useState<Record<string, string>>({});
  const [actionBusy, setActionBusy] = useState(false);
  const actionLock = useRef(false);
  const loadVersion = useRef(0);
  const statusLabel = (value: string) =>
    zh ? (STATUS_LABELS[value] ?? value) : value.replaceAll("_", " ");

  const load = useCallback(async () => {
    const version = ++loadVersion.current;
    setBusy(true);
    setError("");
    try {
      const [account, siteConfig, namePage, saved, orderPage, refundPage, creditPage] =
        await Promise.all([
          loadAccountSummary(),
          loadSiteConfig(),
          loadAccountNames({ limit: 20, site: siteFilter || undefined, status: statuses.names }),
          loadAccountFavorites({
            limit: 20,
            site: siteFilter || undefined,
            status: statuses.favorites,
          }),
          loadAccountOrders({ limit: 20, site: siteFilter || undefined, status: statuses.orders }),
          loadAccountRefunds({
            limit: 20,
            site: siteFilter || undefined,
            status: statuses.refunds,
          }),
          loadAccountCredits({
            limit: 20,
            site: siteFilter || undefined,
            status: statuses.credits,
          }),
        ]);
      if (version !== loadVersion.current) return;
      setSummary(account);
      setConfig(siteConfig);
      setNames(namePage.items);
      setFavorites(saved.items);
      setOrders(orderPage.items);
      setRefunds(refundPage.items);
      setCredits(creditPage.items);
      setCursors({
        names: namePage.nextCursor,
        favorites: saved.nextCursor,
        orders: orderPage.nextCursor,
        refunds: refundPage.nextCursor,
        credits: creditPage.nextCursor,
      });
    } catch (reason) {
      if (version !== loadVersion.current) return;
      if (reason instanceof ApiRequestError && reason.status === 401) {
        window.location.replace(`/login?next=${encodeURIComponent("/account")}`);
        return;
      }
      setError(
        reason instanceof Error
          ? reason.message
          : zh
            ? "账户信息加载失败"
            : "Account data failed to load",
      );
    } finally {
      if (version === loadVersion.current) setBusy(false);
    }
  }, [zh, siteFilter, statuses]);

  useEffect(() => {
    void load();
  }, [load]);

  const selectTab = (value: Tab) => {
    setTab(value);
    const url = new URL(window.location.href);
    url.searchParams.set("tab", value);
    window.history.replaceState({}, "", url);
  };

  const activeRefundOrders = useMemo(() => new Set(refunds.map((item) => item.orderId)), [refunds]);

  const currentSite = zh ? "cn" : "en";
  // 同站记录走相对路径（含本地开发）；异站记录用 config 的对方站域名
  const otherSiteUrl = config?.alternateUrl;
  const resultHref = (recordSite: "cn" | "en", id: string) =>
    recordSite === currentSite
      ? `/?result=${encodeURIComponent(id)}`
      : `${otherSiteUrl ?? ""}/?result=${encodeURIComponent(id)}`;

  async function runAction(action: () => Promise<void>) {
    if (actionLock.current) return;
    actionLock.current = true;
    setActionBusy(true);
    setError("");
    try {
      await action();
    } catch (reason) {
      setError(
        reason instanceof Error
          ? reason.message
          : zh
            ? "操作失败，请重试"
            : "Request failed. Please retry.",
      );
    } finally {
      actionLock.current = false;
      setActionBusy(false);
    }
  }

  async function saveProfile(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const name = String(new FormData(event.currentTarget).get("name") ?? "").trim();
    await runAction(async () => {
      await updateAccountProfile(name);
      await load();
    });
  }

  async function requestRefund(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!refundOrder) return;
    const selectedOrder = refundOrder;
    const form = new FormData(event.currentTarget);
    await runAction(async () => {
      const refund = await submitRefundRequest({
        orderId: selectedOrder.id,
        reason: String(form.get("reason")) as
          | "duplicate"
          | "billing_error"
          | "not_delivered"
          | "other",
        details: String(form.get("details") ?? ""),
      });
      trackEvent("refund_requested", {
        site: refund.site,
        value: refund.requestedAmountMinor / 100,
        currency: refund.currency.toUpperCase(),
      });
      setRefundOrder(null);
      setTab("refunds");
      setStatuses((value) => ({ ...value, refunds: "" }));
    });
  }

  async function loadMore(kind: RecordTab) {
    const cursor = cursors[kind];
    if (!cursor) return;
    const params = { cursor, limit: 20, site: siteFilter || undefined, status: statuses[kind] };
    await runAction(async () => {
      if (kind === "names") {
        const page = await loadAccountNames(params);
        setNames((items) => [...items, ...page.items]);
        setCursors((value) => ({ ...value, names: page.nextCursor }));
      } else if (kind === "orders") {
        const page = await loadAccountOrders(params);
        setOrders((items) => [...items, ...page.items]);
        setCursors((value) => ({ ...value, orders: page.nextCursor }));
      } else if (kind === "refunds") {
        const page = await loadAccountRefunds(params);
        setRefunds((items) => [...items, ...page.items]);
        setCursors((value) => ({ ...value, refunds: page.nextCursor }));
      } else if (kind === "favorites") {
        const page = await loadAccountFavorites(params);
        setFavorites((items) => [...items, ...page.items]);
        setCursors((value) => ({ ...value, favorites: page.nextCursor }));
      } else {
        const page = await loadAccountCredits(params);
        setCredits((items) => [...items, ...page.items]);
        setCursors((value) => ({ ...value, credits: page.nextCursor }));
      }
    });
  }

  if (busy && !summary)
    return (
      <div className="account-hub-loading">
        {zh ? "正在整理账户档案…" : "Preparing your account archive…"}
      </div>
    );

  return (
    <section className="account-hub">
      <header className="account-hub-heading">
        <div>
          <span>{zh ? "个人中心 / MY JIEYI" : "ACCOUNT / MY JIEYI"}</span>
          <h1>{zh ? "你的名字、订单与每一次使用。" : "Your names, orders, and every credit."}</h1>
        </div>
        <button type="button" onClick={() => setPurchaseOpen(true)}>
          <ShoppingBag /> {zh ? "购买次数" : "Buy credits"}
        </button>
      </header>

      {error ? (
        <div className="account-hub-error">
          <p>{error}</p>
          <button type="button" onClick={() => void load()}>
            {zh ? "重新加载" : "Retry"}
          </button>
        </div>
      ) : null}

      <div className="account-hub-layout">
        <nav aria-label={zh ? "账户栏目" : "Account sections"}>
          {TABS.map((item) => {
            const Icon = item.icon;
            return (
              <button
                className={tab === item.id ? "active" : ""}
                key={item.id}
                type="button"
                onClick={() => selectTab(item.id)}
              >
                <Icon /> {zh ? item.zh : item.en}
              </button>
            );
          })}
        </nav>

        <div className="account-hub-content" aria-busy={busy || actionBusy}>
          {tab !== "overview" && tab !== "security" ? (
            <div className="account-filters">
              <label>
                {zh ? "站点" : "Site"}
                <select
                  value={siteFilter}
                  disabled={busy || actionBusy}
                  onChange={(event) => setSiteFilter(event.target.value as "" | "cn" | "en")}
                >
                  <option value="">{zh ? "全部站点" : "Both sites"}</option>
                  <option value="cn">{zh ? "中文站" : "Chinese site"}</option>
                  <option value="en">{zh ? "英文站" : "English site"}</option>
                </select>
              </label>
              <label>
                {zh ? "状态 / 类型" : "Status / Type"}
                <select
                  value={statuses[tab] ?? ""}
                  disabled={busy || actionBusy}
                  onChange={(event) =>
                    setStatuses((value) => ({ ...value, [tab]: event.target.value }))
                  }
                >
                  <option value="">{zh ? "全部" : "All"}</option>
                  {FILTERS[tab].map((value) => (
                    <option key={value} value={value}>
                      {statusLabel(value)}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          ) : null}
          {busy ? <p role="status">{zh ? "正在加载…" : "Loading…"}</p> : null}
          {tab === "overview" && summary ? (
            <>
              <div className="account-profile-card">
                <div className="account-avatar">
                  {summary.user.image ? (
                    <img alt="" src={summary.user.image} />
                  ) : (
                    summary.user.name.slice(0, 1).toUpperCase()
                  )}
                </div>
                <div>
                  <small>{zh ? "账户资料" : "PROFILE"}</small>
                  <strong>{summary.user.name}</strong>
                  <p>{summary.user.email}</p>
                </div>
                <form onSubmit={saveProfile}>
                  <input
                    name="name"
                    defaultValue={summary.user.name}
                    minLength={2}
                    maxLength={50}
                    aria-label={zh ? "昵称" : "Display name"}
                  />
                  <button type="submit" disabled={actionBusy}>
                    {zh ? "保存昵称" : "Save name"}
                  </button>
                </form>
              </div>
              <div className="account-metric-grid">
                <article>
                  <span>{zh ? "中文取名" : "Chinese site"}</span>
                  <strong>{summary.balances.cn}</strong>
                  <small>{zh ? "可用次数" : "credits"}</small>
                </article>
                <article>
                  <span>{zh ? "英文取名" : "English site"}</span>
                  <strong>{summary.balances.en}</strong>
                  <small>{zh ? "可用次数" : "credits"}</small>
                </article>
                <article>
                  <span>{zh ? "取名记录" : "Name files"}</span>
                  <strong>{summary.counts.names}</strong>
                  <small>{zh ? "全部站点" : "both sites"}</small>
                </article>
                <article>
                  <span>{zh ? "购买订单" : "Orders"}</span>
                  <strong>{summary.counts.orders}</strong>
                  <small>{zh ? "含待支付" : "including pending"}</small>
                </article>
              </div>
            </>
          ) : null}

          {!busy && tab === "names" ? (
            <RecordList
              empty={zh ? "还没有取名记录。" : "No naming records yet."}
              emptyWhen={names.length === 0}
            >
              {names.map((item) => (
                <a className="account-record" href={resultHref(item.site, item.id)} key={item.id}>
                  <span className={`site-chip ${item.site}`}>{item.site.toUpperCase()}</span>
                  <div>
                    <strong>
                      {item.result?.names.map((name) => name.fullName).join(" · ") ??
                        (zh ? "生成处理中" : "In progress")}
                    </strong>
                    <small>{time(item.createdAt, language)}</small>
                  </div>
                  <em>{statusLabel(item.status)}</em>
                </a>
              ))}
              <LoadMore
                cursor={cursors.names}
                disabled={actionBusy}
                label={zh ? "加载更多" : "Load more"}
                onClick={() => void loadMore("names")}
              />
            </RecordList>
          ) : null}

          {!busy && tab === "favorites" ? (
            <RecordList
              empty={zh ? "收藏的名字会显示在这里。" : "Saved names will appear here."}
              emptyWhen={favorites.length === 0}
            >
              {favorites.map((item) => (
                <a
                  className="account-record"
                  href={resultHref(item.site, item.requestId)}
                  key={`${item.requestId}-${item.fullName}`}
                >
                  <span className={`site-chip ${item.site}`}>{item.site.toUpperCase()}</span>
                  <div>
                    <strong>{item.fullName}</strong>
                    <small>{time(item.createdAt, language)}</small>
                  </div>
                </a>
              ))}
              <LoadMore
                cursor={cursors.favorites}
                disabled={actionBusy}
                label={zh ? "加载更多" : "Load more"}
                onClick={() => void loadMore("favorites")}
              />
            </RecordList>
          ) : null}

          {!busy && tab === "orders" ? (
            <RecordList
              empty={zh ? "暂无购买记录。" : "No orders yet."}
              emptyWhen={orders.length === 0}
            >
              {orders.map((order) => (
                <article className="account-record order" key={order.id}>
                  <span className={`site-chip ${order.site}`}>{order.site.toUpperCase()}</span>
                  <div>
                    <strong>
                      {money(order.currency, order.amountMinor)} · {order.credits}{" "}
                      {zh ? "次" : "credits"}
                    </strong>
                    <small>
                      {time(order.createdAt, language)} · {order.id.slice(0, 8)}
                    </small>
                  </div>
                  <em>{statusLabel(order.status)}</em>
                  {["paid", "partially_refunded"].includes(order.status) &&
                  !activeRefundOrders.has(order.id) ? (
                    <button type="button" onClick={() => setRefundOrder(order)}>
                      {zh ? "申请退款" : "Request refund"}
                    </button>
                  ) : null}
                </article>
              ))}
              <LoadMore
                cursor={cursors.orders}
                disabled={actionBusy}
                label={zh ? "加载更多" : "Load more"}
                onClick={() => void loadMore("orders")}
              />
            </RecordList>
          ) : null}

          {!busy && tab === "refunds" ? (
            <RecordList
              empty={zh ? "暂无退款申请。" : "No refund requests."}
              emptyWhen={refunds.length === 0}
            >
              {refunds.map((refund) => (
                <article className="account-record" key={refund.id}>
                  <RefreshCw />
                  <div>
                    <strong>{money(refund.currency, refund.requestedAmountMinor)}</strong>
                    <small>
                      {refund.reason} · {time(refund.createdAt, language)}
                    </small>
                    {refund.adminNote ? <p>{refund.adminNote}</p> : null}
                  </div>
                  <em>{statusLabel(refund.status)}</em>
                  {refund.status === "submitted" ? (
                    <button
                      type="button"
                      disabled={actionBusy}
                      onClick={() =>
                        setRefundOrder({
                          id: refund.orderId,
                          currency: refund.currency,
                          amountMinor: refund.requestedAmountMinor,
                          refundedAmountMinor: 0,
                          reason: refund.reason,
                          details: refund.details,
                        })
                      }
                    >
                      {zh ? "编辑申请" : "Edit request"}
                    </button>
                  ) : null}
                </article>
              ))}
              <LoadMore
                cursor={cursors.refunds}
                disabled={actionBusy}
                label={zh ? "加载更多" : "Load more"}
                onClick={() => void loadMore("refunds")}
              />
            </RecordList>
          ) : null}

          {!busy && tab === "credits" ? (
            <RecordList
              empty={zh ? "暂无次数流水。" : "No credit ledger entries."}
              emptyWhen={credits.length === 0}
            >
              {credits.map((entry) => (
                <article className="account-record" key={entry.id}>
                  <span className={`ledger-delta ${entry.delta > 0 ? "plus" : "minus"}`}>
                    {entry.delta > 0 ? "+" : ""}
                    {entry.delta}
                  </span>
                  <div>
                    <strong>{statusLabel(entry.reason)}</strong>
                    <small>
                      {entry.site.toUpperCase()} · {time(entry.createdAt, language)}
                    </small>
                  </div>
                </article>
              ))}
              <LoadMore
                cursor={cursors.credits}
                disabled={actionBusy}
                label={zh ? "加载更多" : "Load more"}
                onClick={() => void loadMore("credits")}
              />
            </RecordList>
          ) : null}

          {tab === "security" && summary ? (
            <div className="security-grid">
              <article>
                <ShieldCheck />
                <div>
                  <strong>{zh ? "登录方式" : "Sign-in methods"}</strong>
                  <p>{summary.providers.join(" · ") || (zh ? "邮箱密码" : "Email and password")}</p>
                </div>
              </article>
              <article>
                <History />
                <div>
                  <strong>{zh ? "注册时间" : "Member since"}</strong>
                  <p>{time(summary.user.createdAt, language)}</p>
                </div>
              </article>
              <button
                type="button"
                disabled={actionBusy}
                onClick={() => void runAction(downloadAccountExport)}
              >
                <Download /> {zh ? "下载我的数据" : "Download my data"}
              </button>
              <button
                className="danger"
                type="button"
                disabled={actionBusy}
                onClick={async () => {
                  if (
                    window.confirm(
                      zh ? "永久删除账户？此操作无法撤销。" : "Permanently delete this account?",
                    )
                  ) {
                    await runAction(async () => {
                      await deleteAccount();
                      window.location.href = "/";
                    });
                  }
                }}
              >
                {zh ? "永久删除账户" : "Delete account permanently"}
              </button>
            </div>
          ) : null}
        </div>
      </div>

      {refundOrder ? (
        <div className="refund-dialog-backdrop">
          <form className="refund-dialog" onSubmit={requestRefund}>
            <CreditCard />
            <h2>
              {refundOrder.details
                ? zh
                  ? "编辑退款申请"
                  : "Edit refund request"
                : zh
                  ? "申请人工退款审核"
                  : "Request a refund review"}
            </h2>
            {error ? <p role="alert">{error}</p> : null}
            <p>
              {money(
                refundOrder.currency,
                refundOrder.amountMinor - refundOrder.refundedAmountMinor,
              )}
            </p>
            <label>
              {zh ? "原因" : "Reason"}
              <select
                name="reason"
                defaultValue={refundOrder.reason ?? "other"}
                disabled={actionBusy}
              >
                <option value="duplicate">{zh ? "重复扣款" : "Duplicate charge"}</option>
                <option value="billing_error">{zh ? "计费错误" : "Billing error"}</option>
                <option value="not_delivered">{zh ? "未完成交付" : "Not delivered"}</option>
                <option value="other">{zh ? "其他" : "Other"}</option>
              </select>
            </label>
            <label>
              {zh ? "详细说明" : "Details"}
              <textarea
                name="details"
                minLength={10}
                maxLength={1000}
                required
                defaultValue={refundOrder.details ?? ""}
                disabled={actionBusy}
              />
            </label>
            <div>
              <button type="button" disabled={actionBusy} onClick={() => setRefundOrder(null)}>
                {zh ? "取消" : "Cancel"}
              </button>
              <button type="submit" disabled={actionBusy}>
                {actionBusy
                  ? zh
                    ? "正在提交…"
                    : "Submitting…"
                  : zh
                    ? "提交审核"
                    : "Submit review"}
              </button>
            </div>
          </form>
        </div>
      ) : null}

      <PurchaseModal
        language={language}
        open={purchaseOpen}
        products={config?.products ?? []}
        paymentEnabled={config?.features.payment ?? false}
        onClose={() => setPurchaseOpen(false)}
        onRefresh={async () => {
          await load();
        }}
      />
    </section>
  );
}

function RecordList({
  children,
  empty,
  emptyWhen,
}: {
  children: ReactNode;
  empty: string;
  emptyWhen: boolean;
}) {
  return <div className="account-record-list">{emptyWhen ? <p>{empty}</p> : children}</div>;
}

function LoadMore({
  cursor,
  disabled,
  label,
  onClick,
}: {
  cursor?: string | null;
  disabled?: boolean;
  label: string;
  onClick: () => void;
}) {
  return cursor ? (
    <button className="account-load-more" type="button" onClick={onClick} disabled={disabled}>
      {label}
    </button>
  ) : null;
}
